import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent, onConsentOpen } from "@/lib/consent";

/**
 * Cookie consent banner for the public site. Shows until the visitor makes a
 * choice; non-essential trackers (the Meta Pixel) stay off until "Accept". A
 * footer "Cookie settings" link (openConsentSettings) reopens it to change or
 * withdraw consent. Client-only — renders nothing during SSR/first paint to
 * avoid a hydration mismatch.
 *
 * Buyer pages (e.g. the WhatsApp delivery status page) mark <html data-buyer-lang="<lang>"> (react-helmet
 * htmlAttributes): the banner then speaks the page's language — not the site's — and is one slim row on phones so it
 * doesn't sit over the page's buttons. Every other page is unchanged.
 */
const buyerLang = (): string | null =>
  typeof document !== "undefined" ? document.documentElement.getAttribute("data-buyer-lang") : null;

const CookieConsent = () => {
  const { t: siteT, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // Read after mount (Helmet sets the attribute on the client): a buyer page's language and compact layout.
  const [buyer, setBuyer] = useState<string | null>(null);
  useEffect(() => {
    setBuyer(buyerLang());
    const obs = new MutationObserver(() => setBuyer(buyerLang()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-buyer-lang"] });
    return () => obs.disconnect();
  }, []);
  const t = (buyer ? i18n.getFixedT(buyer) : siteT) as typeof siteT;

  useEffect(() => {
    setOpen(!getConsent().decided);
    return onConsentOpen(() => setOpen(true));
  }, []);

  if (!open) return null;

  const choose = (analytics: boolean) => {
    setConsent(analytics);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("cookieConsent.aria", "Cookie consent")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      data-compact={buyer ? "true" : undefined}
    >
      <div
        className={
          buyer
            ? "mx-auto flex max-w-xl items-center gap-3 px-4 py-2"
            : "container mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
        }
      >
        <p className={buyer ? "flex-1 text-xs leading-snug text-muted-foreground" : "text-sm text-muted-foreground flex-1"}>
          {buyer
            ? t("cookieConsent.short", "We use necessary cookies. Analytics only if you allow.")
            : t(
                "cookieConsent.message",
                "We use strictly necessary cookies to run the site, and — only with your consent — analytics and advertising cookies to understand usage and measure ads.",
              )}{" "}
          {buyer && <br />}
          <Link to="/privacy" className="underline hover:text-foreground">
            {t("cookieConsent.learnMore", "How we use cookies")}
          </Link>
          .
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" className={buyer ? "h-8 px-3 text-xs" : undefined} onClick={() => choose(false)}>
            {t("cookieConsent.decline", "Decline")}
          </Button>
          <Button size="sm" className={buyer ? "h-8 px-3 text-xs" : undefined} onClick={() => choose(true)}>
            {t("cookieConsent.accept", "Accept all")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
